(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const routeRoot = window.Shopify?.routes?.root || '/';
  const money = (cents, currency = 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
  let activeCleanup = null;

  function mount() {
    activeCleanup?.();
    const host = document.querySelector('[data-kinora-product]');
    if (!host) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const cleanup = [];
    const listen = (el, type, fn, options = {}) => el?.addEventListener(type, fn, { ...options, signal });

    const form = host.querySelector('.kn-product-form');
    const addButton = form?.querySelector('[data-add-to-cart]');
    const addLabel = form?.querySelector('[data-add-label]');
    const status = form?.querySelector('[data-form-status]');
    const sticky = host.querySelector('[data-sticky]');
    const stickyButton = host.querySelector('[data-sticky-add]');
    const stickyLabel = host.querySelector('[data-sticky-price]');
    const stickyCompare = host.querySelector('[data-sticky-compare]');
    const stickyGift = host.querySelector('[data-sticky-gift]');
    const currency = host.dataset.currency || 'USD';
    const selectedPack = () => form?.querySelector('[data-pack-choice]:checked');
    const nativeQuantity = form?.querySelector('[data-pack-quantity]');
    let stickyAnimation = null;
    let stickyVisible = false;
    function setSticky(visible) {
      if (!sticky || stickyVisible === visible) return;
      stickyVisible = visible;
      stickyAnimation?.cancel();
      sticky.inert = !visible;
      if (visible) {
        sticky.hidden = false;
        if (!reduced.matches && sticky.animate) stickyAnimation = sticky.animate([
          { transform: 'translateY(calc(100% + 12px))', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 }
        ], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' });
      } else if (!reduced.matches && sticky.animate && !sticky.hidden) {
        stickyAnimation = sticky.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(calc(100% + 12px))', opacity: 0 }], { duration: 180, easing: 'ease-in' });
        stickyAnimation.onfinish = () => { if (!stickyVisible) sticky.hidden = true; };
      } else sticky.hidden = true;
    }
    const baseBodyPadding = sticky?.hidden ? (parseFloat(getComputedStyle(document.body).paddingBottom) || 0) : 0;
    const updateSticky = () => {
      // Ignore the bar's own bottom spacing so the 40% boundary cannot oscillate.
      const addedPadding = Math.max(0, (parseFloat(getComputedStyle(document.body).paddingBottom) || 0) - baseBodyPadding);
      const distance = document.documentElement.scrollHeight - innerHeight - addedPadding;
      const progress = distance > 0 ? Math.max(0, scrollY) / distance : 0;
      setSticky(Boolean(progress >= .4 && host.dataset.variantId && addButton && !addButton.disabled));
    };
    const setPurchaseBusy = busy => {
      addButton.disabled = busy;
      if (stickyButton) stickyButton.disabled = busy;
      const quick = form.querySelector('[data-product-quick-checkout]');
      if (quick) quick.inert = busy;
      updateSticky();
    };
    const updatePack = () => {
      const pack = selectedPack();
      if (!pack) return;
      const total = money(Number(pack.dataset.packTotal), currency);
      const reference = money(Number(pack.dataset.packCompare), currency);
      if (nativeQuantity && nativeQuantity.value !== pack.value) {
        nativeQuantity.value = pack.value;
        nativeQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const heroPrice = host.querySelector('[data-pack-price]');
      const heroReference = host.querySelector('[data-pack-reference]');
      if (heroPrice) heroPrice.textContent = total;
      if (heroReference) heroReference.textContent = reference;
      if (addLabel && !addButton.disabled) addLabel.textContent = `Add to cart — ${total}`;
      if (stickyLabel) stickyLabel.textContent = total;
      if (stickyCompare) stickyCompare.textContent = reference;
      if (stickyGift) { const half = Number(pack.value) / 2; stickyGift.textContent = `Buy ${half}, get ${half} FREE`; }
    };
    form?.querySelectorAll('[data-pack-choice]').forEach(input => listen(input, 'change', updatePack));
    updatePack();
    listen(form, 'submit', async event => {
      // Shopify's own accelerated checkout owns its submission and payment flow.
      if (event.submitter && event.submitter !== addButton) return;
      if (!window.fetch || !host.dataset.variantId) return;
      event.preventDefault();
      if (addButton.disabled) return;
      const returnFocus = document.activeElement === stickyButton ? stickyButton : addButton;
      setPurchaseBusy(true);
      addButton.setAttribute('aria-busy', 'true');
      const originalLabel = addLabel.textContent;
      addLabel.textContent = 'Adding to your cart…';
      status.textContent = '';
      try {
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(host.dataset.variantId), quantity: Number(selectedPack()?.value || 2) }] }), signal
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'We could not add this pack. Please try again.');
        if (window.KinoraCart) {
          window.KinoraCart.startTimer?.();
          await window.KinoraCart.open({ refresh: true, returnFocus });
          setPurchaseBusy(false);
          addButton.removeAttribute('aria-busy');
          updatePack();
        } else window.location.assign(`${routeRoot}cart`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        status.textContent = error.message;
        setPurchaseBusy(false);
        addButton.removeAttribute('aria-busy');
        addLabel.textContent = originalLabel;
      }
    });
    listen(host.querySelector('[data-sticky-add]'), 'click', () => { if (addButton && !addButton.disabled) form.requestSubmit(addButton); });
    // Scroll progress is sampled at most once per frame.
    let stickyFrame = 0;
    const scheduleSticky = () => {
      if (stickyFrame) return;
      stickyFrame = requestAnimationFrame(() => { stickyFrame = 0; updateSticky(); });
    };
    listen(window, 'scroll', scheduleSticky, { passive: true });
    listen(window, 'resize', scheduleSticky, { passive: true });
    scheduleSticky();
    cleanup.push(() => cancelAnimationFrame(stickyFrame));
    if ('ResizeObserver' in window) {
      const pageSizing = new ResizeObserver(scheduleSticky);
      pageSizing.observe(document.querySelector('main') || host);
      cleanup.push(() => pageSizing.disconnect());
    }
    listen(reduced, 'change', () => {
      stickyAnimation?.cancel();
      if (sticky) sticky.hidden = !stickyVisible;
    });
    cleanup.push(() => stickyAnimation?.cancel());

    if (sticky && 'ResizeObserver' in window) {
      const sizing = new ResizeObserver(() => {
        if (!sticky.hidden) document.documentElement.style.setProperty('--kn-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
      });
      sizing.observe(sticky);
      cleanup.push(() => sizing.disconnect());
    }

    host.querySelectorAll('details').forEach(details => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.kn-detail-body');
      if (!summary || !content) return;
      let animation = null;
      let expanding = null;
      listen(summary, 'click', event => {
        if (reduced.matches || !Element.prototype.animate) return;
        event.preventDefault();
        const opening = expanding === null ? !details.open : !expanding;
        const initial = details.getBoundingClientRect().height;
        animation?.cancel();
        details.open = true;
        details.style.overflow = 'hidden';
        const target = opening ? summary.getBoundingClientRect().height + content.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        expanding = opening;
        animation = details.animate({ height: [`${initial}px`, `${target}px`] }, { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
        animation.onfinish = () => {
          details.open = opening;
          details.style.overflow = '';
          expanding = null;
          animation = null;
        };
      });
      cleanup.push(() => animation?.cancel());
    });
    activeCleanup = () => { controller.abort(); cleanup.forEach(fn => fn()); };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true }); else mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', () => activeCleanup?.());
})();
